import React from 'react';
import { Bar } from 'react-chartjs-2';
import { VIZ, tooltipStyle, formatNumber } from '../chartTheme';

/**
 * Part-to-whole as a single 100% stacked bar plus a legend that carries the
 * counts. Preferred over a pie: shares stay comparable along one axis, and two
 * or three slices don't become a novelty circle. Segments are separated by a
 * 2px gap in the surface colour, not by a stroke.
 */
export default function ShareBar({ labels, data, valueFormatter = formatNumber }) {
  const total = data.reduce((sum, value) => sum + (value || 0), 0) || 1;
  // Four named hues in fixed order; a folded tail stays deliberately recessive.
  const segments = labels.map((label, index) => ({
    label,
    value: data[index] || 0,
    share: ((data[index] || 0) / total) * 100,
    color: label === 'Other' ? VIZ.muted : VIZ.categorical[index % VIZ.categorical.length],
  }));

  return (
    <div className="flex h-full flex-col">
      <div className="h-[72px]">
        <Bar
          data={{
            labels: [''],
            datasets: segments.map((segment) => ({
              label: segment.label,
              data: [segment.share],
              backgroundColor: segment.color,
              borderColor: VIZ.surface,
              borderWidth: { left: 1, right: 1, top: 0, bottom: 0 },
              borderRadius: 4,
              borderSkipped: false,
              maxBarThickness: 28,
            })),
          }}
          options={{
            indexAxis: 'y',
            maintainAspectRatio: false,
            scales: {
              x: { stacked: true, display: false, max: 100 },
              y: { stacked: true, display: false },
            },
            plugins: {
              legend: { display: false },
              tooltip: {
                ...tooltipStyle((value) => `${Number(value).toFixed(1)}%`),
                callbacks: {
                  title: () => '',
                  label: (ctx) => {
                    const segment = segments[ctx.datasetIndex];
                    return `${valueFormatter(segment.value)} (${segment.share.toFixed(1)}%)  ·  ${segment.label}`;
                  },
                },
              },
            },
          }}
        />
      </div>

      {/* Legend doubles as the direct-label layer: identity never rests on colour alone. */}
      <ul className="mt-2 grid grid-cols-2 gap-x-4 gap-y-1.5 overflow-auto text-left">
        {segments.map((segment) => (
          <li key={segment.label} className="flex items-center gap-2 text-xs">
            <span className="h-2.5 w-2.5 shrink-0 rounded-sm" style={{ backgroundColor: segment.color }} />
            <span className="truncate text-slate-600" title={segment.label}>
              {segment.label}
            </span>
            <span className="ml-auto shrink-0 font-semibold text-slate-800">
              {valueFormatter(segment.value)}
            </span>
            <span className="w-11 shrink-0 text-right text-slate-400">{segment.share.toFixed(0)}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
